import { useEffect, useRef, useState } from "react";
import { BsStars } from "react-icons/bs";
import { StyledAICampaign } from "./AICampaign.styles";
import Form from '../../molecules/Form';
import Button from '../../molecules/Button';
import Toast from "../../molecules/Toast";
import campaignService from "../../../services/campaignService";
import offerService from "../../../services/offerService";
import useOfferForm from "./useOfferForm";
import Loaders from "../Loaders";
import {
    CompanyIdentityFields,
    OfferDetailsFields,
    EmailLengthSelector,
    PersonalizationFields,
    OutreachPreferenceFields,
} from "./OfferFormSections";

const AICampaign = ({ setData, setStep, data, isEdit, variant = 'campaign', offerId, onComplete, onCancel }) => {
    const isOfferFlow = variant === 'offer';
    const isOfferEdit = isOfferFlow && !!offerId;
    const offerForm = useOfferForm({ isEdit: isEdit || isOfferEdit, offerId });
    const [aiCampaignLoading, setAiCampaignLoading] = useState(false);
    const hasUnsavedChangesRef = useRef(false);

    hasUnsavedChangesRef.current = offerForm.hasUnsavedChanges;

    useEffect(() => {
        if (!isOfferEdit) return undefined;

        const handleUnsavedCheck = event => {
            if (event.detail) {
                event.detail.hasUnsavedChanges = hasUnsavedChangesRef.current;
            }
        };

        window.addEventListener('offer-form-unsaved-check', handleUnsavedCheck);
        return () => window.removeEventListener('offer-form-unsaved-check', handleUnsavedCheck);
    }, [isOfferEdit]);

    const handleSubmit = async () => {
        const offerPayload = offerForm.buildPayload();
        const campaignPayload = offerForm.buildPayload({
            listId: data?.step1?.list_id || undefined,
            campaignId: data?.step2?.campaignId || undefined,
        });
        if (isEdit && offerForm.taskId && !isOfferFlow) {
            setStep?.(6);
            return;
        }
        try {
            setAiCampaignLoading(true);
            const response = isOfferFlow
                ? (isOfferEdit
                    ? await offerService.updateOffer(offerId, offerPayload)
                    : await offerService.createOffer(offerPayload))
                : await campaignService.AiCampaign(campaignPayload);
            if (response.success) {
                Toast({
                    type: 'success',
                    message: isOfferEdit
                        ? 'Offer updated successfully'
                        : isOfferFlow
                            ? 'Offer saved successfully'
                            : response.message,
                });
                offerForm.markAsSaved();
                setData?.(prev => ({
                    ...prev,
                    ...(isOfferFlow ? { offer: response.data || offerPayload } : { step11: campaignPayload }),
                }));
                if (isOfferFlow) {
                    onComplete?.();
                } else {
                    setStep?.(6);
                }
            }
        } catch (error) {
            Toast({ type: 'error', message: error.message });
        }
        finally {
            setAiCampaignLoading(false);
        }
    }

    if (isOfferEdit && offerForm.offerLoading) {
        return <Loaders viewLoader />;
    }

    return (
        <StyledAICampaign $disableForm={isEdit && offerForm.taskId && !isOfferFlow} $isOfferFlow={isOfferFlow}>

            <div className="content-holder">
                {!isOfferEdit && (
                    <>
                        <h1 className="title">Teach AI About Your Offer</h1>
                        <p className="description">
                            This helps Houdiny generate personalized outreach based on what you offer.
                        </p>
                    </>
                )}
                <Form form={offerForm.form} onSubmit={handleSubmit} onTouched={offerForm.handleFormChange}>
                    <div className="fields-wrapper">
                        <CompanyIdentityFields />
                        <Button
                            className="auto-fill-btn"
                            type="button"
                            variant="gradient"
                            loader={offerForm.autoFillLoading}
                            disabled={!offerForm.canAutoFill || offerForm.autoFillLoading}
                            onClick={() => offerForm.autoFillFields()}>
                            <BsStars color="var(--white)" size={18} />
                            Auto-Fill Key Fields by AI
                        </Button>
                        <OfferDetailsFields offerForm={offerForm} />
                        <EmailLengthSelector activeTab={offerForm.activeTab} setActiveTab={offerForm.setActiveTab} />
                        <PersonalizationFields />
                        <OutreachPreferenceFields offerForm={offerForm} />
                    </div>
                    <div className="btn-holder">
                        {!isOfferFlow && (
                            <Button
                                rounded
                                variant="outline"
                                width="155px"
                                type="button"
                                onClick={() => setStep?.(2)}>
                                Back
                            </Button>
                        )}
                        <Button
                            newStyle
                            rounded
                            variant="darkBlue"
                            loader={aiCampaignLoading}
                            disabled={aiCampaignLoading || (isOfferEdit && offerForm.offerLoading)}
                            width={isOfferFlow ? '200px' : '155px'}
                            type="submit">
                            {isOfferEdit ? 'Update Offer' : isOfferFlow ? 'Save Offer Details' : 'Next'}
                        </Button>
                    </div>
                </Form>
            </div>
        </StyledAICampaign>
    );
};
export default AICampaign;
